import { filterClasses } from "@swivl-mono/helpers";
import type { ReactElement } from "react";

interface WidgetSwitchProps {
  on: boolean;
  onSet: (on: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

/**
 * Widget-scoped on/off toggle (Design Audit 991:1694).
 *
 * A local restyle of the design-system `Switch`, kept inside the widgets feature
 * so we can iterate on the look WITHOUT changing the shared component (which is
 * used across studio). Promote into `libs/ui` once the style is finalized.
 *
 * 30×18 pill: teal-800 on / grey-400 off, darkening on hover (teal-900 / grey-500);
 * disabled dims the track (grey-200 off, teal-800 @ 50% on). White knob slides.
 */
export function WidgetSwitch({ on, onSet, disabled = false, ariaLabel }: WidgetSwitchProps): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onSet(!on)}
      className={filterClasses(
        "relative inline-flex h-[18px] w-[30px] shrink-0 rounded-full transition-colors duration-200",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        on ? (disabled ? "bg-teal-800/50" : "bg-teal-800 hover:bg-teal-900") : disabled ? "bg-grey-200" : "bg-grey-400 hover:bg-grey-500"
      )}
    >
      <span
        className={filterClasses(
          "absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-white shadow-sm transition-all duration-200",
          on ? "left-[14px]" : "left-0.5"
        )}
      />
    </button>
  );
}
